import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Skills {
  technical: number;
  communication: number;
  problemSolving: number;
  teamwork: number;
  leadership: number;
  [key: string]: number;
}

interface SkillsRadarChartProps {
  skills: Skills;
  candidateName?: string;
  showTitle?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function SkillsRadarChart({ 
  skills, 
  candidateName, 
  showTitle = false,
  size = 'md'
}: SkillsRadarChartProps) {
  // Always use core 5 skills for the radar chart to avoid overcrowding
  const coreSkills = [
    { key: 'technical', label: 'Technical', shortLabel: 'Tech' },
    { key: 'communication', label: 'Communication', shortLabel: 'Comm' },
    { key: 'problemSolving', label: 'Problem Solving', shortLabel: 'Problem' },
    { key: 'teamwork', label: 'Teamwork', shortLabel: 'Team' },
    { key: 'leadership', label: 'Leadership', shortLabel: 'Lead' }
  ].filter(skill => skills[skill.key] !== undefined && skills[skill.key] > 0);



  // Calculate average score for core skills only
  const averageScore = coreSkills.length > 0 
    ? coreSkills.reduce((sum, skill) => sum + skills[skill.key], 0) / coreSkills.length 
    : 0;

  const getScoreTextColor = (score: number) => {
    if (score >= 8) return 'text-green-700';
    if (score >= 6) return 'text-blue-700';
    if (score >= 4) return 'text-yellow-700';
    return 'text-red-700';
  };

  // Size configurations optimized for small cards
  const sizeConfig = {
    sm: { 
      chartSize: 140, 
      radius: 45, 
      labelOffset: 25, 
      fontSize: '8px',
      centerFontSize: 'text-xs',
      padding: 'p-2',
      margin: 15,
      showLabels: false // Hide labels on small charts to avoid clutter
    },
    md: { 
      chartSize: 200, 
      radius: 65, 
      labelOffset: 35, 
      fontSize: '9px',
      centerFontSize: 'text-sm',
      padding: 'p-3',
      margin: 25,
      showLabels: true
    },
    lg: { 
      chartSize: 240, 
      radius: 80, 
      labelOffset: 45, 
      fontSize: '10px',
      centerFontSize: 'text-lg',
      padding: 'p-4',
      margin: 30,
      showLabels: true
    }
  };

  const config = sizeConfig[size];

  const SkillsRadarChart = () => {
    const chartSize = config.chartSize;
    const totalSize = chartSize + (config.margin * 2);
    const center = totalSize / 2;
    const radius = config.radius;

    if (coreSkills.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
          No skills data available
        </div>
      );
    }

    // Calculate points for the maximum boundary (each skill at 10/10)
    const maxBoundaryPoints = coreSkills.map((skill, index) => {
      const angle = (index * (360 / coreSkills.length) - 90) * (Math.PI / 180);
      const x = center + Math.cos(angle) * radius;
      const y = center + Math.sin(angle) * radius;
      
      return {
        x,
        y,
        skill,
        angle
      };
    });

    // Calculate actual data points based on skill scores
    const dataPoints = coreSkills.map((skill, index) => {
      const scale = skills[skill.key] / 10;
      const angle = (index * (360 / coreSkills.length) - 90) * (Math.PI / 180);
      return {
        x: center + Math.cos(angle) * radius * scale,
        y: center + Math.sin(angle) * radius * scale,
        value: skills[skill.key],
        skill
      };
    });

    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="relative">
          <svg 
            width={totalSize} 
            height={totalSize} 
            viewBox={`0 0 ${totalSize} ${totalSize}`}
            className="w-full h-auto drop-shadow-sm"
          >
            {/* Background concentric circles */}
            {[2, 4, 6, 8, 10].map((level) => {
              const scale = level / 10;
              return (
                <circle
                  key={level}
                  cx={center}
                  cy={center}
                  r={radius * scale}
                  fill="none"
                  stroke="#e2e8f0"
                  strokeWidth="1"
                  opacity={0.3}
                />
              );
            })}
            
            {/* Grid lines from center to each skill point */}
            {maxBoundaryPoints.map((point, index) => (
              <line
                key={index}
                x1={center}
                y1={center}
                x2={point.x}
                y2={point.y}
                stroke="#e2e8f0"
                strokeWidth="1"
                opacity={0.2}
              />
            ))}
            
            {/* Maximum boundary polygon */}
            <polygon
              points={maxBoundaryPoints.map(p => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="#94a3b8"
              strokeWidth="1.5"
              opacity="0.5"
            />
            
            {/* Candidate's actual skill polygon */}
            <polygon
              points={dataPoints.map(p => `${p.x},${p.y}`).join(' ')}
              fill="#3b82f6"
              fillOpacity="0.25"
              stroke="#3b82f6"
              strokeWidth="2"
            />
            
            {/* Candidate's skill points */}
            {dataPoints.map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r="3"
                fill="#3b82f6"
                stroke="white"
                strokeWidth="1.5"
              />
            ))}

            {/* Skill corner points */}
            {maxBoundaryPoints.map((corner, index) => (
              <circle
                key={index}
                cx={corner.x}
                cy={corner.y}
                r="2"
                fill="#64748b"
                stroke="white"
                strokeWidth="1"
                opacity="0.6"
              />
            ))}

            {/* Center average score */}
            <circle
              cx={center}
              cy={center}
              r="16"
              fill="white"
              stroke="#e2e8f0"
              strokeWidth="1.5"
              opacity="0.95"
            />
            <text
              x={center}
              y={center - 1}
              textAnchor="middle"
              dominantBaseline="middle"
              className={cn("fill-current font-bold", config.centerFontSize, getScoreTextColor(averageScore))}
            >
              {averageScore.toFixed(1)}
            </text>
            <text
              x={center}
              y={center + 8}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-slate-500"
              fontSize="7px"
            >
              avg
            </text>
          </svg>
        </div>
      </div>
    );
  };

  const ChartContent = () => (
    <div className={cn("bg-slate-50 rounded-lg border border-slate-200", config.padding)}>
      <SkillsRadarChart />
    </div>
  );

  if (showTitle) {
    return (
      <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-900">
            {candidateName ? `${candidateName}'s Skills` : 'Skills Assessment'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <ChartContent />
        </CardContent>
      </Card>
    );
  }

  return <ChartContent />;
} 